import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Waypoints, RotateCw, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RecordCard } from "@/components/RecordCard";
import { SiteFooter } from "@/components/BrandFooter";

const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);

const Stat = ({ k, v, sub, tone = "text-white", testid }) => (
  <div className="border border-white/10 bg-atlas-surface/70 px-4 py-3 min-w-0">
    <div className="text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/85">{k}</div>
    <div className={`text-2xl font-mono leading-tight ${tone}`} data-testid={testid}>{v}</div>
    {sub && <div className="text-[10px] font-mono text-atlas-secondary truncate">{sub}</div>}
  </div>
);

export default function DamRecord() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.damRecord().then(setData).catch(() => setData({ items: [], summary: {} })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.running && (data?.items?.length ?? 0) >= (data?.total_cases ?? 0)) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [data, load]);

  const rerun = async () => {
    const r = await api.rerunDamRecord();
    toast(r.started ? "Replaying every case through the current detector…" : "A replay is already running");
    load();
  };
  const s = data?.summary || {};
  const items = data?.items || [];

  return (
    <div className="h-screen overflow-y-auto bg-atlas-void text-atlas-foreground grid-backdrop" data-testid="dam-record-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/" data-testid="dam-record-back" className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-atlas-secondary hover:text-white mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Observatory
        </Link>
        <div className="flex items-start justify-between gap-6 mb-2">
          <div className="flex items-center gap-3">
            <Waypoints className="w-7 h-7 text-sky-400" />
            <h1 className="text-4xl sm:text-5xl font-display text-white">Dam Detection Record</h1>
          </div>
          <button onClick={rerun} disabled={data?.running} data-testid="dam-record-rerun"
            className="shrink-0 flex items-center gap-1.5 px-3 h-8 border border-sky-400/40 text-[10px] font-mono uppercase tracking-wider text-sky-300 hover:bg-sky-400 hover:text-atlas-void disabled:opacity-50 transition-colors">
            <RotateCw className={`w-3.5 h-3.5 ${data?.running ? "animate-spin" : ""}`} /> {data?.running ? "Replaying" : "Re-run replay"}
          </button>
        </div>
        <p className="text-sm text-atlas-secondary max-w-3xl mb-8">
          Documented landslide dams from the Sentinel-1 era, replayed through the exact detector Vigilo Earth runs live.
          Every radar pass during the lake's documented lifetime is compared with the last same-orbit pass before the
          dam formed, just as the 12-hour scan would have done. Each case also gets a quiet control: the same river,
          same season, one year earlier, which measures false alarms. Misses are shown, not hidden.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3" data-testid="dam-record-summary">
          <Stat k="Hit rate" v={pct(s.hit_rate)} sub={`${s.detected ?? 0} of ${(s.detected ?? 0) + (s.missed ?? 0)} dams`} tone="text-emerald-400" testid="dam-hit-rate" />
          <Stat k="Hit rate when radar saw it" v={pct(s.hit_rate_when_observed)} sub="excludes revisit gaps" testid="dam-hit-rate-observed" />
          <Stat k="Missed · revisit gap" v={s.missed_revisit_gap ?? 0} sub="lake gone before next pass" tone="text-atlas-orange" testid="dam-missed-gap" />
          <Stat k="False alarms" v={`${s.false_alarms ?? 0} / ${s.controls ?? 0}`} sub={`rate ${pct(s.false_alarm_rate)} · quiet controls`} tone={s.false_alarms ? "text-atlas-red" : "text-white"} testid="dam-false-alarms" />
          <Stat k="Detector" v={`v${s.detector_version ?? "—"}`} sub={s.thresholds ? `lake ≥ ${s.thresholds.lake_km2} km² · VV < ${s.thresholds.water_db} dB` : ""} testid="dam-detector-version" />
        </div>
        <p className="flex items-center gap-1.5 text-[10px] font-mono text-atlas-secondary mb-10" data-testid="dam-record-progress">
          <ShieldCheck className="w-3.5 h-3.5 text-sky-400" />
          {items.length} of {data?.total_cases ?? "…"} cases replayed{data?.running ? " · replay running" : ""} · Copernicus Sentinel-1 GRD via CDSE
        </p>

        {data === null ? (
          <div className="text-[11px] font-mono text-atlas-secondary">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-white/15 p-10 text-center text-[12px] text-atlas-secondary" data-testid="dam-record-empty">
            Replay in progress. Each case renders several Sentinel-1 scenes, so this takes a few minutes.
          </div>
        ) : (
          <div className="flex flex-col gap-6" data-testid="dam-record-list">
            {items.map((r, i) => <RecordCard key={r.id} r={r} i={i} />)}
          </div>
        )}
        <p className="mt-10 text-[10px] font-mono text-atlas-secondary/85 flex items-center gap-1.5">
          <ExternalLink className="w-3 h-3" /> Each case links to its published source for the dam date and location.
        </p>
        <SiteFooter />
      </div>
    </div>
  );
}
